import React from 'react';
import { Users, Eye, PlusCircle, ChefHat, Store, Utensils, AlertCircle } from 'lucide-react';
import { useLanguage } from "../../context/LanguageContext";

const CanteenCard = ({
  canteen,
  isSurveyComplete,
  surveyDate,
  onViewCanteen,
  onNewSurvey,
  getTypeName,
  getSurveyType,
  showSurveyList,
  surveyListContent,
  surveyData = []
}) => {
  const { t } = useLanguage();

  const getTypeIcon = (type) => {
    if (type === t.global.type_kiosk) return <Store size={16} className="mr-1" />;
    if (type === t.global.type_kitchen) return <ChefHat size={16} className="mr-1" />;
    if (type === t.global.type_kitchen_cum_dining) return <><ChefHat size={16} className="mr-1" /><Utensils size={16} className="mr-1" /></>;
    return <Users size={16} className="mr-1" />;
  };

  const typeName = getTypeName(canteen.canteen_type);
  const surveyType = getSurveyType(canteen.canteen_type);

  const canViewSurvey = surveyType === "kiosk" || surveyType === "kitchen" || surveyType === "kitchendining";

  const hasIncompleteSurvey = surveyData.some(survey => !survey.total_survey_completed);

  const getLatestIncompleteSurvey = () => {
    const incompleteSurveys = surveyData.filter(survey => !survey.total_survey_completed);
    if (incompleteSurveys.length === 0) return null;
    return incompleteSurveys.sort((a, b) =>
      new Date(b.created_at) - new Date(a.created_at)
    )[0];
  };

  const latestIncompleteSurvey = getLatestIncompleteSurvey();

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      const today = new Date();
      const isToday = date.toDateString() === today.toDateString();
      if (isToday) {
        return `${t.global.today} ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
      } else {
        return date.toLocaleDateString('en-US', {
          weekday: 'long', year: 'numeric', month: 'long',
          day: 'numeric', hour: '2-digit', minute: '2-digit'
        });
      }
    } catch (error) {
      return dateString;
    }
  };

  const isSurveyDateToday = (dateString) => {
    if (!dateString) return false;
    try {
      const date = new Date(dateString);
      const today = new Date();
      return date.toDateString() === today.toDateString();
    } catch (error) {
      return false;
    }
  };

  return (
    <div className="border rounded-lg p-4 hover:border-blue-300 transition mb-4">
      {/* Canteen Info */}
      <div className="flex justify-between items-start mb-3">
        <div>
          <div className="font-bold text-lg text-gray-800 flex items-center flex-wrap gap-1">
            {getTypeIcon(typeName)}
            {canteen.area_name}
            {isSurveyComplete && isSurveyDateToday(surveyDate) && (
              <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full flex items-center">
                <span className="mr-1">✓</span>
                {t.global.survey_completed_today}
              </span>
            )}
            {surveyDate && !isSurveyDateToday(surveyDate) && (
              <span className="ml-2 px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                {t.global.survey_completed}
              </span>
            )}
            {hasIncompleteSurvey && (
              <span className="ml-2 px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full flex items-center">
                <AlertCircle size={12} className="mr-1" />
                {t.global.incomplete_survey}
              </span>
            )}
          </div>
          <div className="text-sm text-gray-600 mt-1">
            <span className="inline-flex items-center">
              {t.global.type_label}: <span className="font-medium ml-1">{typeName}</span>
            </span>
            <span className="mx-2">•</span>
            {t.global.canteen_id_label}: <span className="font-medium">{canteen.id}</span>
          </div>
          {canteen.parent_kitchen && (
            <div className="text-xs mt-1 text-blue-600">
              {t.global.parent_kitchen}: {canteen.parent_kitchen.area_name}
            </div>
          )}
          {surveyDate && (
            <div className="text-xs mt-2">
              <span className={`px-2 py-1 rounded ${isSurveyDateToday(surveyDate) ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}`}>
                {formatDate(surveyDate)}
              </span>
            </div>
          )}
          {latestIncompleteSurvey && (
            <div className="text-xs mt-2">
              <span className="px-2 py-1 rounded bg-orange-100 text-orange-800 flex items-center">
                <AlertCircle size={12} className="mr-1" />
                {t.global.pending_survey}: {formatDate(latestIncompleteSurvey.created_at)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex space-x-3">
        {canViewSurvey && (
          <button
            onClick={() => onViewCanteen(canteen)}
            className={`flex-1 py-2 px-4 rounded-lg transition flex items-center justify-center ${
              hasIncompleteSurvey
                ? 'bg-red-50 hover:bg-red-100 text-red-700 border border-red-200'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            }`}
          >
            <Eye className="mr-2" size={16} />
            {t.global.view_surveys} {hasIncompleteSurvey && `(${surveyData.filter(s => !s.total_survey_completed).length} ${t.global.incomplete})`}
          </button>
        )}

        {/* Commented out original disabled button logic
        <button
          onClick={() => onNewSurvey(canteen)}
          disabled={isSurveyComplete && isSurveyDateToday(surveyDate)}
          className={`flex-1 py-2 px-4 rounded-lg transition flex items-center justify-center ${
            isSurveyComplete && isSurveyDateToday(surveyDate)
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : hasIncompleteSurvey
              ? 'bg-orange-600 hover:bg-orange-700 text-white'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
          }`}
        >
          <PlusCircle className="mr-2" size={16} />
          {isSurveyComplete && isSurveyDateToday(surveyDate)
            ? t.global.survey_completed_today
            : hasIncompleteSurvey
            ? t.global.resume_incomplete_survey
            : t.global.new_survey}
        </button>
        */}
        <button
          onClick={() => onNewSurvey(canteen)}
          className={`flex-1 py-2 px-4 rounded-lg transition flex items-center justify-center ${
            hasIncompleteSurvey
              ? 'bg-orange-600 hover:bg-orange-700 text-white'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
          }`}
        >
          <PlusCircle className="mr-2" size={16} />
          {hasIncompleteSurvey
            ? t.global.resume_incomplete_survey
            : t.global.new_survey}
        </button>
      </div>

      {/* Survey List */}
      {showSurveyList && surveyListContent}
    </div>
  );
};

export default CanteenCard;